import { useEffect, useState } from "react";
import { api, fmtDuration, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/Layout";
import { AddAssetWizard } from "@/components/AddAssetWizard";
import { Modal, ModalField, ModalTextarea, ModalSelect } from "@/pages/Channels";
import { MagnifyingGlass, Clock, Plus, Trash, Broadcast, FilmSlate, Copy, ArrowSquareOut, PencilSimple, CheckCircle, Info, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

const HLS_RE = /^https?:\/\/.+\.m3u8(\?.*)?$/i;

const PROVIDER_CLS = {
  ReVine: "text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/20",
  "Bunny.net": "text-orange-400 bg-orange-500/10 border-orange-500/20",
  "AWS S3": "text-amber-500 bg-amber-500/10 border-amber-500/20",
  "Cloudflare R2": "text-orange-500 bg-orange-500/10 border-orange-500/20",
  Wasabi: "text-green-400 bg-green-500/10 border-green-500/20",
  "Custom HLS": "text-violet-400 bg-violet-500/10 border-violet-500/20",
  Other: "text-neutral-400 bg-neutral-500/10 border-neutral-500/20",
};

export default function AssetLibrary() {
  const [assets, setAssets] = useState(null);
  const [types, setTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [provider, setProvider] = useState("all");
  const [showWizard, setShowWizard] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [verify, setVerify] = useState(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "operator"].includes(user?.role);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (type !== "all") params.asset_type = type;
    if (provider !== "all") params.provider = provider;
    return api.get("/assets", { params }).then((r) => setAssets(r.data));
  };
  useEffect(() => {
    api.get("/assets/types").then((r) => setTypes(r.data));
    api.get("/assets/providers").then((r) => setProviders(r.data));
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, type, provider]);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this asset from the library?")) return;
    try { await api.delete(`/assets/${id}`); toast.success("Asset removed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove asset"); }
  };

  const startEdit = () => {
    setDraft({
      name: detail.name || "", description: detail.description || "",
      asset_type: detail.asset_type || "Other", provider: detail.provider || "ReVine",
      hls_url: detail.hls_url || "", duration_seconds: detail.duration_seconds || 0,
      resolution: detail.resolution || "1080p", aspect_ratio: detail.aspect_ratio || "16:9",
      content_category: detail.content_category || "", tags: (detail.tags || []).join(", "),
      provider_asset_id: detail.provider_asset_id || "", notes: detail.notes || "",
      epg_title: detail.epg_title || "",
    });
    setVerify(null); setEditing(true);
  };
  const draftDirty = () => {
    if (!draft) return false;
    return draft.name !== (detail.name || "") || draft.description !== (detail.description || "") ||
      draft.asset_type !== (detail.asset_type || "Other") || draft.provider !== (detail.provider || "ReVine") ||
      draft.hls_url !== (detail.hls_url || "") || String(draft.duration_seconds) !== String(detail.duration_seconds || 0) ||
      draft.resolution !== (detail.resolution || "1080p") || draft.aspect_ratio !== (detail.aspect_ratio || "16:9") ||
      draft.content_category !== (detail.content_category || "") || draft.tags !== ((detail.tags || []).join(", ")) ||
      draft.provider_asset_id !== (detail.provider_asset_id || "") || draft.notes !== (detail.notes || "") ||
      draft.epg_title !== (detail.epg_title || "");
  };
  const cancelEdit = () => {
    if (draftDirty() && !window.confirm("Discard unsaved changes?")) return;
    setEditing(false); setDraft(null); setVerify(null);
  };
  const closeDetail = () => {
    if (editing && draftDirty() && !window.confirm("Discard unsaved changes?")) return;
    setDetail(null); setEditing(false); setDraft(null); setVerify(null);
  };
  const runVerify = async () => {
    if (!HLS_RE.test((draft.hls_url || "").trim())) {
      setVerify({ verified: false, message: "Enter a valid http(s) .m3u8 URL to verify." });
      return;
    }
    setVerify({ loading: true });
    try { const { data } = await api.post("/assets/verify", { hls_url: draft.hls_url.trim() }); setVerify(data); }
    catch { setVerify({ verified: false, message: "Verification could not be performed from the server; the asset may still be valid and can be saved." }); }
  };
  const saveAsset = async () => {
    if (!draft.name.trim()) { toast.error("Title is required"); return; }
    if (!HLS_RE.test((draft.hls_url || "").trim())) { toast.error("Playback URL must be a valid http(s) .m3u8 URL"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/assets/${detail.id}`, {
        name: draft.name.trim(), description: draft.description, asset_type: draft.asset_type,
        provider: draft.provider, hls_url: draft.hls_url.trim(),
        duration_seconds: parseInt(draft.duration_seconds, 10) || 0, resolution: draft.resolution,
        aspect_ratio: draft.aspect_ratio, content_category: draft.content_category,
        tags: draft.tags.split(",").map((t) => t.trim()).filter(Boolean),
        provider_asset_id: draft.provider_asset_id, notes: draft.notes,
        epg_title: draft.epg_title,
      });
      toast.success("Asset updated");
      setDetail(data); setEditing(false); setDraft(null); setVerify(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Asset Library" subtitle="Registered playable assets · references only, no media stored"
        actions={canEdit && (
          <button data-testid="add-asset-btn" onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm">
            <Plus size={16} weight="bold" /> Add Existing Asset
          </button>
        )} />

      <div className="p-8 ccs-enter">
        <div className="flex flex-col lg:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
            <input data-testid="asset-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…"
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm pl-9 pr-3 py-2 text-sm outline-none transition-colors duration-150" />
          </div>
          <select data-testid="asset-type-filter" value={type} onChange={(e) => setType(e.target.value)}
            className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
            <option value="all">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="asset-provider-filter" value={provider} onChange={(e) => setProvider(e.target.value)}
            className="bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
            <option value="all">All providers</option>
            {providers.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {!assets ? <Loading /> : assets.length === 0 ? (
          <div className="text-center py-24 text-[#737373]">
            <FilmSlate size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No assets found. Register an existing playable asset to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {assets.map((m) => (
              <div key={m.id} data-testid={`asset-card-${m.id}`} onClick={() => setDetail(m)}
                className="bg-[#141414] border border-[#262626] rounded-sm overflow-hidden hover:border-[#3A86FF] cursor-pointer transition-colors duration-150 group">
                <div className="aspect-video bg-[#0A0A0A] overflow-hidden relative">
                  {m.thumbnail
                    ? <img src={m.thumbnail} alt={m.name} loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300" />
                    : <div className="w-full h-full flex items-center justify-center"><Broadcast size={24} className="text-[#333]" /></div>}
                  {m.duration_seconds > 0 && (
                    <div className="absolute bottom-2 right-2 bg-black/80 px-1.5 py-0.5 rounded-sm font-mono text-[10px] flex items-center gap-1">
                      <Clock size={10} /> {fmtDuration(m.duration_seconds)}
                    </div>
                  )}
                  <span className={`absolute top-2 left-2 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${PROVIDER_CLS[m.provider] || PROVIDER_CLS.Other}`}>{m.provider}</span>
                  {canEdit && m.org_id === user.org_id && (
                    <button data-testid={`asset-delete-${m.id}`} onClick={(e) => remove(m.id, e)}
                      className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center bg-black/70 rounded-sm text-[#737373] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all duration-150"><Trash size={13} /></button>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-mono text-[10px] text-[#3A86FF] mb-0.5" data-testid={`asset-ref-${m.id}`}>{m.ref || "—"}</div>
                  <h3 className="text-sm font-medium truncate">{m.name}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] font-mono text-[#525252] uppercase tracking-wider">{m.asset_type}</span>
                    <span className="text-[10px] font-mono text-[#525252]">{m.resolution}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showWizard && <AddAssetWizard onClose={() => setShowWizard(false)} onCreated={() => { setShowWizard(false); load(); }} />}

      {detail && (
        <Modal title={editing ? "Edit Asset" : "Asset Details"} onClose={closeDetail} wide>
          {!editing ? (
            <div className="space-y-4">
              <div className="aspect-video bg-[#0A0A0A] border border-[#262626] rounded-sm overflow-hidden flex items-center justify-center">
                {detail.thumbnail ? <img src={detail.thumbnail} alt="" className="w-full h-full object-cover" /> : <Broadcast size={28} className="text-[#333]" />}
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold mb-0.5">Internal Asset Reference</div>
                  <div className="font-mono text-sm text-[#3A86FF]" data-testid="detail-asset-ref">{detail.ref || "—"}</div>
                  <h3 className="font-head font-semibold text-lg mt-1">{detail.name}</h3>
                </div>
                {canEdit && detail.org_id === user.org_id && (
                  <button data-testid="edit-asset-btn" onClick={startEdit}
                    className="flex items-center gap-1.5 text-xs text-[#3A86FF] hover:underline shrink-0 mt-1">
                    <PencilSimple size={14} /> Edit
                  </button>
                )}
              </div>
              {detail.description && <p className="text-sm text-[#A3A3A3]">{detail.description}</p>}
              <div className="space-y-0.5">
                <DetailRow label="Provider" value={detail.provider} />
                <DetailRow label="Provider Asset ID" value={detail.provider_asset_id || "—"} />
                <DetailRow label="EPG Display Title" value={<span data-testid="detail-epg-title">{detail.epg_title || <span className="text-[#525252]">Same as title</span>}</span>} />
                <DetailRow label="Asset Type" value={detail.asset_type} />
                <DetailRow label="Category" value={detail.content_category || "—"} />
                <DetailRow label="Duration" value={detail.duration_seconds ? fmtDuration(detail.duration_seconds) : "—"} />
                <DetailRow label="Resolution" value={`${detail.resolution} · ${detail.aspect_ratio || "16:9"}`} />
                <DetailRow label="Tags" value={(detail.tags || []).length ? detail.tags.join(", ") : "—"} />
                <DetailRow label="Status" value={<span className="text-emerald-500 uppercase font-mono text-[11px]">{detail.status}</span>} />
                <DetailRow label="Date Added" value={fmtDate(detail.created_at)} />
                <DetailRow label="Last Updated" value={fmtDate(detail.updated_at)} />
              </div>
              {detail.notes && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Notes (operator)</div>
                  <p className="text-sm text-[#A3A3A3] whitespace-pre-wrap">{detail.notes}</p>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Playback URL</div>
                <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded-sm p-2">
                  <code className="font-mono text-[10px] text-emerald-400 truncate flex-1">{detail.hls_url}</code>
                  <button data-testid="detail-copy-url" onClick={() => { navigator.clipboard.writeText(detail.hls_url); toast.success("Copied"); }}
                    className="text-[#737373] hover:text-white transition-colors duration-150"><Copy size={14} /></button>
                  <a href={detail.hls_url} target="_blank" rel="noreferrer" className="text-[#737373] hover:text-[#3A86FF] transition-colors duration-150"><ArrowSquareOut size={14} /></a>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-[10px] font-mono text-[#525252]">Ref {detail.ref || "—"} · added {fmtDate(detail.created_at)} · not editable</div>
              <ModalField label="Title" testid="edit-asset-name"
                value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Asset title" />
              <div>
                <ModalField label="EPG Display Title (optional)" testid="edit-asset-epg-title"
                  value={draft.epg_title} onChange={(e) => setDraft({ ...draft, epg_title: e.target.value })} placeholder={draft.name || "Same as title"} />
                <p className="text-[10px] text-[#525252] mt-1.5">Shown in programme guides only. Leave blank to use the title above. The asset itself is not renamed.</p>
              </div>
              <ModalTextarea label="Description" testid="edit-asset-description"
                value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Short description" />
              <div className="grid grid-cols-2 gap-4">
                <ModalSelect label="Asset Type" testid="edit-asset-type"
                  value={draft.asset_type} onChange={(e) => setDraft({ ...draft, asset_type: e.target.value })}>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </ModalSelect>
                <ModalSelect label="Provider" testid="edit-asset-provider"
                  value={draft.provider} onChange={(e) => setDraft({ ...draft, provider: e.target.value })}>
                  {providers.map((p) => <option key={p} value={p}>{p}</option>)}
                </ModalSelect>
              </div>
              <div>
                <ModalField label="Playback URL (HLS .m3u8)" testid="edit-asset-url"
                  value={draft.hls_url} onChange={(e) => { setDraft({ ...draft, hls_url: e.target.value }); setVerify(null); }} placeholder="https://…/index.m3u8" />
                <div className="flex items-center gap-3 mt-1.5">
                  <button data-testid="verify-asset-url-btn" onClick={runVerify} disabled={verify?.loading}
                    className="flex items-center gap-1.5 text-xs text-[#3A86FF] hover:underline disabled:opacity-60">
                    {verify?.loading ? <SpinnerGap size={13} className="animate-spin" /> : <CheckCircle size={13} />} Verify URL (optional)
                  </button>
                </div>
                {verify && !verify.loading && (
                  <div data-testid="verify-result" className={`flex items-start gap-1.5 mt-2 text-[11px] rounded-sm border px-2.5 py-2 ${verify.verified ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" : "text-amber-400 bg-amber-500/10 border-amber-500/20"}`}>
                    {verify.verified ? <CheckCircle size={14} className="shrink-0 mt-px" /> : <Info size={14} className="shrink-0 mt-px" />}
                    <span>{verify.message}</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <ModalField label="Duration (sec)" testid="edit-asset-duration" type="number" min="0"
                  value={draft.duration_seconds} onChange={(e) => setDraft({ ...draft, duration_seconds: e.target.value })} />
                <ModalField label="Resolution" testid="edit-asset-resolution"
                  value={draft.resolution} onChange={(e) => setDraft({ ...draft, resolution: e.target.value })} placeholder="1080p" />
                <ModalField label="Aspect Ratio" testid="edit-asset-aspect"
                  value={draft.aspect_ratio} onChange={(e) => setDraft({ ...draft, aspect_ratio: e.target.value })} placeholder="16:9" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ModalField label="Category" testid="edit-asset-category"
                  value={draft.content_category} onChange={(e) => setDraft({ ...draft, content_category: e.target.value })} placeholder="e.g. Faith, Drama" />
                <ModalField label="Provider Asset ID" testid="edit-asset-provider-id"
                  value={draft.provider_asset_id} onChange={(e) => setDraft({ ...draft, provider_asset_id: e.target.value })} placeholder="External ID" />
              </div>
              <ModalField label="Tags (comma separated)" testid="edit-asset-tags"
                value={draft.tags} onChange={(e) => setDraft({ ...draft, tags: e.target.value })} placeholder="e.g. classic, family" />
              <ModalTextarea label="Notes (operator only)" testid="edit-asset-notes"
                value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Internal notes" />
              <div className="flex items-center gap-3 pt-1">
                <button data-testid="save-asset-btn" onClick={saveAsset} disabled={saving}
                  className="flex-1 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm disabled:opacity-60">
                  {saving ? "Saving…" : "Save Changes"}
                </button>
                <button data-testid="cancel-asset-edit-btn" onClick={cancelEdit}
                  className="px-5 py-2.5 rounded-sm border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#3A3A3A] transition-colors duration-150 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-[#1c1c1c] py-2">
      <span className="text-xs text-[#737373]">{label}</span>
      <span className="text-sm text-right">{value}</span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
